// QuickProductForm.jsx
// IMPROVED: Error display, re-save functionality, better status handling

import React, {
  useState,
  useCallback,
  useMemo,
  memo,
  useEffect,
  useRef,
} from "react";
import {
  Package,
  Scale,
  Gem,
  Factory,
  Shield,
  Eye,
  Image,
  ChevronDown,
  Save,
  Info,
  Zap,
  Lock,
  Unlock,
  FileText,
  Settings,
  Plus,
  Trash2,
  Store,
  AlertCircle,
  RefreshCw,
  CheckCircle2,
  XCircle,
} from "lucide-react";

// ✅ USE YOUR EXISTING COMPONENTS
import CategoryHierarchyDropdown from "pages/Category/components/CategoryHierarchyDropdown";
import MediaUpload from "components/common/MediaUpload";
import StockManagementSection from "./StockManagementSection";
import { HallmarkManagementSection } from "components/products/ProductManagement/AdvancedComponents";

import { useMultiProduct } from "../../../context/MultiProductContext";
import { useSelector } from "react-redux";
import { TAB_STATUS } from "./hooks/useProductTabs";

// ============= CONSTANTS =============
const PRODUCT_TYPES = [
  { value: "GOLD", label: "Gold", icon: "🥇" },
  { value: "SILVER", label: "Silver", icon: "🥈" },
  { value: "PLATINUM", label: "Platinum", icon: "💎" },
  { value: "DIAMOND", label: "Diamond", icon: "💠" },
  { value: "STUDDED", label: "Studded", icon: "✨" },
];

const PRODUCT_SUBTYPES = [
  "RING",
  "NECKLACE",
  "EARRING",
  "BRACELET",
  "PENDANT",
  "CHAIN",
  "BANGLE",
  "ANKLET",
  "NOSE_PIN",
  "MANGALSUTRA",
  "KADA",
  "COIN",
  "BAR",
  "IDOL",
  "OTHER",
];

const PURITIES = {
  GOLD: ["24K", "22K", "18K", "14K"],
  SILVER: ["925", "950"],
  PLATINUM: ["PT950"],
  DIAMOND: ["18K", "14K", "PT950"],
  STUDDED: ["22K", "18K", "14K"],
};

const METAL_COLORS = ["YELLOW", "WHITE", "ROSE", "TWO_TONE", "THREE_TONE"];
const GENDERS = ["MALE", "FEMALE", "UNISEX", "KIDS"];
const STATUSES = ["ACTIVE", "INACTIVE", "DRAFT", "ARCHIVED"];
const CRAFT_TYPES = [
  "HANDMADE",
  "MACHINE_MADE",
  "CAST",
  "HANDCRAFTED",
  "STAMPED",
];
const TECHNIQUES = [
  "KUNDAN",
  "MEENAKARI",
  "TEMPLE",
  "FILIGREE",
  "ANTIQUE",
  "MODERN",
  "TRADITIONAL",
];
const STONE_TYPES = [
  "DIAMOND",
  "RUBY",
  "EMERALD",
  "SAPPHIRE",
  "PEARL",
  "TOPAZ",
  "AMETHYST",
  "OTHER",
];
const STONE_SHAPES = [
  "ROUND",
  "PRINCESS",
  "OVAL",
  "MARQUISE",
  "PEAR",
  "CUSHION",
  "EMERALD",
  "ASSCHER",
  "RADIANT",
  "HEART",
];
const STONE_SETTINGS = [
  "PRONG",
  "BEZEL",
  "CHANNEL",
  "PAVE",
  "TENSION",
  "HALO",
  "CLUSTER",
];

const MODE_STORAGE_KEY = "multiProductCreator_formMode";

// ============================================
// STATUS BANNER COMPONENT
// ============================================

const StatusBanner = memo(({ status, lastError, onReSave }) => {
  // if (status === TAB_STATUS.SAVED) {
  //   return (
  //     <div className="flex items-center gap-3 p-3 bg-emerald-50 border border-emerald-200 rounded-xl mb-4">
  //       <CheckCircle2 size={20} className="text-emerald-500 flex-shrink-0" />
  //       <div className="flex-1">
  //         <p className="text-sm font-medium text-emerald-700">
  //           Product saved successfully!
  //         </p>
  //         <p className="text-xs text-emerald-600 mt-0.5">
  //           You can edit and save again to update the product.
  //         </p>
  //       </div>
  //     </div>
  //   );
  // }

  if (status === TAB_STATUS.FAILED) {
    return (
      <div className="flex items-start gap-3 p-3 bg-red-50 border border-red-200 rounded-xl mb-4">
        <XCircle size={20} className="text-red-500 flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <p className="text-sm font-medium text-red-700">
            Failed to save product
          </p>
          {lastError && (
            <p className="text-xs text-red-600 mt-0.5">{lastError}</p>
          )}
          <p className="text-xs text-red-600 mt-1">
            Fix any issues and try saving again.
          </p>
        </div>
        <button
          onClick={onReSave}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 flex-shrink-0"
        >
          <RefreshCw size={14} />
          Retry
        </button>
      </div>
    );
  }

  // if (status === TAB_STATUS.EDITED) {
  //   return (
  //     <div className="flex items-start gap-3 p-3 bg-amber-50 border border-amber-200 rounded-xl mb-4">
  //       <AlertCircle
  //         size={20}
  //         className="text-amber-500 flex-shrink-0 mt-0.5"
  //       />
  //       <div className="flex-1">
  //         <p className="text-sm font-medium text-amber-700">
  //           Product was edited
  //         </p>
  //         <p className="text-xs text-amber-600 mt-0.5">
  //           Changes detected. Save again to update.
  //         </p>
  //       </div>
  //       <button
  //         onClick={onReSave}
  //         className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-600 text-white text-sm font-medium rounded-lg hover:bg-amber-700 flex-shrink-0"
  //       >
  //         <Save size={14} />
  //         Save
  //       </button>
  //     </div>
  //   );
  // }

  // if (status === TAB_STATUS.QUEUED || status === TAB_STATUS.SAVING) {
  //   return (
  //     <div className="flex items-center gap-3 p-3 bg-blue-50 border border-blue-200 rounded-xl mb-4">
  //       <div className="animate-spin">
  //         <RefreshCw size={20} className="text-blue-500" />
  //       </div>
  //       <div className="flex-1">
  //         <p className="text-sm font-medium text-blue-700">
  //           {status === TAB_STATUS.QUEUED
  //             ? "Queued for saving..."
  //             : "Saving..."}
  //         </p>
  //         <p className="text-xs text-blue-600 mt-0.5">
  //           Please wait while the product is being saved.
  //         </p>
  //       </div>
  //     </div>
  //   );
  // }

  return null;
});
StatusBanner.displayName = "StatusBanner";

// ============================================
// LIGHTWEIGHT FORM COMPONENTS
// ============================================

const FormSection = memo(
  ({
    title,
    icon: Icon,
    children,
    defaultOpen = true,
    badge,
    required = false,
  }) => {
    const [isOpen, setIsOpen] = useState(defaultOpen);

    return (
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition-colors"
        >
          <div className="flex items-center gap-2.5">
            <div className="p-1.5 bg-gray-100 rounded-lg">
              <Icon size={16} className="text-gray-600" />
            </div>
            <span className="font-medium text-gray-900 text-sm">{title}</span>
            {required && <span className="text-red-500 text-xs">*</span>}
            {badge !== undefined && badge > 0 && (
              <span className="px-1.5 py-0.5 bg-gray-100 text-gray-600 text-xs rounded-full">
                {badge}
              </span>
            )}
          </div>
          <ChevronDown
            size={18}
            className={`text-gray-400 transition-transform duration-200 ${
              isOpen ? "rotate-180" : ""
            }`}
          />
        </button>
        {isOpen && <div className="px-4 pb-4 pt-1">{children}</div>}
      </div>
    );
  }
);
FormSection.displayName = "FormSection";

const SmartInput = memo(
  ({
    label,
    name,
    type = "text",
    value,
    onChange,
    placeholder,
    required,
    error,
    suffix,
    prefix,
    className = "",
    disabled = false,
    ...props
  }) => (
    <div className={className}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {label}
          {required && <span className="text-red-500 ml-0.5">*</span>}
        </label>
      )}
      <div className="relative">
        {prefix && (
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">
            {prefix}
          </span>
        )}
        <input
          type={type}
          name={name}
          value={value ?? ""}
          onChange={onChange}
          placeholder={placeholder}
          disabled={disabled}
          className={`w-full px-3 py-2 border rounded-lg text-sm transition-colors focus:outline-none focus:ring-1 focus:ring-gray-900 focus:border-gray-900 ${
            error ? "border-red-300 bg-red-50" : "border-gray-200 bg-white"
          } ${prefix ? "pl-7" : ""} ${suffix ? "pr-10" : ""} ${
            disabled ? "bg-gray-100 cursor-not-allowed" : ""
          }`}
          {...props}
        />
        {suffix && (
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">
            {suffix}
          </span>
        )}
      </div>
      {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
    </div>
  )
);
SmartInput.displayName = "SmartInput";

const SmartSelect = memo(
  ({
    label,
    name,
    value,
    onChange,
    options,
    placeholder,
    required,
    error,
    className = "",
    disabled = false,
  }) => (
    <div className={className}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {label}
          {required && <span className="text-red-500 ml-0.5">*</span>}
        </label>
      )}
      <select
        name={name}
        value={value ?? ""}
        onChange={onChange}
        disabled={disabled}
        className={`w-full px-3 py-2 border rounded-lg text-sm appearance-none bg-white focus:outline-none focus:ring-1 focus:ring-gray-900 focus:border-gray-900 ${
          error ? "border-red-300 bg-red-50" : "border-gray-200"
        } ${disabled ? "bg-gray-100 cursor-not-allowed" : ""}`}
        style={{
          backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
          backgroundPosition: "right 0.5rem center",
          backgroundSize: "1.25em 1.25em",
          backgroundRepeat: "no-repeat",
          paddingRight: "2rem",
        }}
      >
        {placeholder && <option value="">{placeholder}</option>}
        {options.map((opt) => (
          <option
            key={typeof opt === "string" ? opt : opt.value}
            value={typeof opt === "string" ? opt : opt.value}
          >
            {typeof opt === "string" ? opt.replace(/_/g, " ") : opt.label}
          </option>
        ))}
      </select>
      {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
    </div>
  )
);
SmartSelect.displayName = "SmartSelect";

const ToggleGroup = memo(({ options, value, onChange, disabled = false }) => (
  <div className="flex flex-wrap gap-1.5">
    {options.map((opt) => {
      const optValue = typeof opt === "string" ? opt : opt.value;
      const optLabel = typeof opt === "string" ? opt : opt.label;
      return (
        <button
          key={optValue}
          type="button"
          onClick={() => !disabled && onChange(optValue)}
          disabled={disabled}
          className={`px-2.5 py-1.5 text-xs sm:text-sm rounded-lg font-medium transition-colors ${
            value === optValue
              ? "bg-gray-900 text-white"
              : "bg-gray-100 text-gray-600 hover:bg-gray-200"
          } ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
        >
          {optLabel.replace(/_/g, " ")}
        </button>
      );
    })}
  </div>
));
ToggleGroup.displayName = "ToggleGroup";

const TypeCard = memo(({ type, isSelected, onClick, disabled = false }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className={`flex flex-col items-center gap-1 p-2 sm:p-3 rounded-lg border-2 transition-colors ${
      isSelected
        ? "border-gray-900 bg-gray-900 text-white"
        : "border-gray-200 bg-white text-gray-600 hover:border-gray-300"
    } ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
  >
    <span className="text-lg sm:text-xl">{type.icon}</span>
    <span className="text-xs font-medium">{type.label}</span>
  </button>
));
TypeCard.displayName = "TypeCard";

// ============================================
// MAIN FORM COMPONENT
// ============================================

const QuickProductForm = ({
  tabId,
  initialData,
  status,
  lastError,
  onSave,
  onDataChange,
  onReSave,
}) => {
  const {
    categories,
    branches,
    user,
    displayMedia,
    mediaFiles,
    handleMediaChange,
    handleMediaUpload,
    handleMediaDelete,
  } = useMultiProduct();

  const [formData, setFormData] = useState(initialData);
  const [errors, setErrors] = useState({});
  const [lockWeights, setLockWeights] = useState(false);
  const [mode, setMode] = useState(() => {
    try {
      return localStorage.getItem(MODE_STORAGE_KEY) || "quick";
    } catch {
      return "quick";
    }
  });

  const { branches: availableBranchesData } = useSelector(
    (state) => state.branch
  );

  const debounceRef = useRef(null);
  const prevTabId = useRef(tabId);

  // Determine if form is read-only (during save)
  const isReadOnly =
    status === TAB_STATUS.QUEUED || status === TAB_STATUS.SAVING;

  // Can show save button
  const canSave =
    status === TAB_STATUS.DRAFT ||
    status === TAB_STATUS.FAILED ||
    status === TAB_STATUS.EDITED ||
    status === TAB_STATUS.SAVED;

  // Get available branches
  const availableBranches = useMemo(() => {
    if (!availableBranchesData) return [];
    return availableBranchesData
      .map((branch) => branch?.code || branch)
      .filter(Boolean);
  }, [availableBranchesData]);

  const permissions = useMemo(
    () => ({ canManageStock: true, canViewStock: true }),
    []
  );

  // Location stock handlers
  const locationStocks = useMemo(
    () => formData.stock?.locationStock || [],
    [formData.stock?.locationStock]
  );

  const addLocationStock = useCallback((branchCode, storeCode = null) => {
    setFormData((prev) => ({
      ...prev,
      stock: {
        ...prev.stock,
        locationStock: [
          ...(prev.stock?.locationStock || []),
          {
            branchCode,
            storeCode,
            quantity: 1,
            reserved: 0,
            available: 1,
            displayQuantity: 0,
            lastUpdated: new Date(),
          },
        ],
      },
    }));
  }, []);

  const updateLocationStock = useCallback((index, field, value) => {
    setFormData((prev) => {
      const locationStock = [...(prev.stock?.locationStock || [])];
      locationStock[index] = { ...locationStock[index], [field]: value };
      if (field === "quantity" || field === "reserved") {
        locationStock[index].available =
          (locationStock[index].quantity || 0) -
          (locationStock[index].reserved || 0);
      }
      const totalQuantity = locationStock.reduce(
        (sum, loc) => sum + (parseInt(loc.quantity) || 0),
        0
      );
      const totalReserved = locationStock.reduce(
        (sum, loc) => sum + (parseInt(loc.reserved) || 0),
        0
      );
      return {
        ...prev,
        stock: {
          ...prev.stock,
          locationStock,
          totalQuantity,
          reservedQuantity: totalReserved,
          availableQuantity: totalQuantity - totalReserved,
        },
      };
    });
  }, []);

  const removeLocationStock = useCallback((index) => {
    setFormData((prev) => {
      const locationStock = (prev.stock?.locationStock || []).filter(
        (_, i) => i !== index
      );
      const totalQuantity = locationStock.reduce(
        (sum, loc) => sum + (parseInt(loc.quantity) || 0),
        0
      );
      const totalReserved = locationStock.reduce(
        (sum, loc) => sum + (parseInt(loc.reserved) || 0),
        0
      );
      return {
        ...prev,
        stock: {
          ...prev.stock,
          locationStock,
          totalQuantity,
          reservedQuantity: totalReserved,
          availableQuantity: totalQuantity - totalReserved,
        },
      };
    });
  }, []);

  const calculateTotalStock = useCallback(() => {
    return locationStocks.reduce(
      (totals, loc) => ({
        quantity: totals.quantity + (parseInt(loc.quantity) || 0),
        reserved: totals.reserved + (parseInt(loc.reserved) || 0),
        available: totals.available + (parseInt(loc.available) || 0),
      }),
      { quantity: 0, reserved: 0, available: 0 }
    );
  }, [locationStocks]);

  // Stone handlers
  const addStone = useCallback(() => {
    setFormData((prev) => ({
      ...prev,
      hasStones: true,
      stones: [
        ...(prev.stones || []),
        {
          type: "DIAMOND",
          weight: 0,
          count: 1,
          color: "",
          clarity: "",
          cut: "",
          shape: "ROUND",
          settingType: "PRONG",
          certification: {
            agency: "",
            certificateNumber: "",
            certificateUrl: "",
          },
          ratePerCarat: 0,
          totalValue: 0,
        },
      ],
    }));
  }, []);

  const updateStone = useCallback((index, field, value) => {
    setFormData((prev) => {
      const stones = [...(prev.stones || [])];
      if (field.startsWith("certification.")) {
        const certField = field.split(".")[1];
        stones[index] = {
          ...stones[index],
          certification: { ...stones[index].certification, [certField]: value },
        };
      } else {
        stones[index] = { ...stones[index], [field]: value };
      }
      if (field === "ratePerCarat" || field === "weight") {
        const weight = field === "weight" ? value : stones[index].weight;
        const rate =
          field === "ratePerCarat" ? value : stones[index].ratePerCarat;
        stones[index].totalValue =
          (parseFloat(weight) || 0) * (parseFloat(rate) || 0);
      }
      const totalStoneWeight = stones.reduce(
        (sum, stone) => sum + (parseFloat(stone.weight) || 0),
        0
      );
      const stoneCharges = stones.reduce(
        (sum, stone) => sum + (parseFloat(stone.totalValue) || 0),
        0
      );
      return {
        ...prev,
        stones,
        totalStoneWeight,
        stoneCharges,
        isStone: stones.length > 0,
        stoneWeight: totalStoneWeight,
      };
    });
  }, []);

  const removeStone = useCallback((index) => {
    setFormData((prev) => {
      const stones = (prev.stones || []).filter((_, i) => i !== index);
      const totalStoneWeight = stones.reduce(
        (sum, stone) => sum + (parseFloat(stone.weight) || 0),
        0
      );
      const stoneCharges = stones.reduce(
        (sum, stone) => sum + (parseFloat(stone.totalValue) || 0),
        0
      );
      return {
        ...prev,
        stones,
        hasStones: stones.length > 0,
        totalStoneWeight,
        stoneCharges,
        isStone: stones.length > 0,
        stoneWeight: totalStoneWeight,
      };
    });
  }, []);

  // Save mode preference
  useEffect(() => {
    try {
      localStorage.setItem(MODE_STORAGE_KEY, mode);
    } catch {}
  }, [mode]);

  // Reset form when tab changes
  useEffect(() => {
    if (prevTabId.current !== tabId) {
      setFormData(initialData);
      setErrors({});
      prevTabId.current = tabId;
    }
  }, [tabId, initialData]);

  // Debounced parent update
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      onDataChange(formData);
    }, 500);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [formData, onDataChange]);

  const handleChange = useCallback(
    (e) => {
      if (isReadOnly) return;
      const { name, value, type, checked } = e.target;
      setFormData((prev) => {
        if (name.includes(".")) {
          const keys = name.split(".");
          const newData = { ...prev };
          let current = newData;
          for (let i = 0; i < keys.length - 1; i++) {
            current[keys[i]] = { ...current[keys[i]] };
            current = current[keys[i]];
          }
          current[keys[keys.length - 1]] =
            type === "checkbox" ? checked : value;
          return newData;
        }
        return { ...prev, [name]: type === "checkbox" ? checked : value };
      });
      if (errors[name]) setErrors((prev) => ({ ...prev, [name]: null }));
    },
    [errors, isReadOnly]
  );

  const updateField = useCallback(
    (name, value) => {
      if (isReadOnly) return;
      setFormData((prev) => {
        if (name.includes(".")) {
          const keys = name.split(".");
          const newData = { ...prev };
          let current = newData;
          for (let i = 0; i < keys.length - 1; i++) {
            current[keys[i]] = { ...current[keys[i]] };
            current = current[keys[i]];
          }
          current[keys[keys.length - 1]] = value;
          return newData;
        }
        return { ...prev, [name]: value };
      });
    },
    [isReadOnly]
  );

  const handleCategoryChange = useCallback(
    (categoryId) => {
      if (isReadOnly) return;
      setFormData((prev) => ({
        ...prev,
        categories: categoryId ? [categoryId] : [],
      }));
    },
    [isReadOnly]
  );

  const handleGrossWeightChange = useCallback(
    (e) => {
      if (isReadOnly) return;
      const value = e.target.value;
      const grossWeight = value === "" ? "" : parseFloat(value);
      setFormData((prev) => ({
        ...prev,
        grossWeight,
        netWeight:
          !lockWeights &&
          !prev.hasStones &&
          typeof grossWeight === "number" &&
          !isNaN(grossWeight)
            ? grossWeight
            : prev.netWeight,
      }));
    },
    [lockWeights, isReadOnly]
  );

  const availablePurities = useMemo(
    () => PURITIES[formData.type] || PURITIES.GOLD,
    [formData.type]
  );
  const selectedCategoryId = useMemo(
    () => formData.categories?.[0] || null,
    [formData.categories]
  );

  const validate = useCallback(() => {
    const newErrors = {};
    if (!formData.name || formData.name.trim().length < 3)
      newErrors.name = "Name required (min 3 chars)";
    if (!formData.type) newErrors.type = "Type required";
    if (!formData.subType) newErrors.subType = "Subtype required";
    if (!formData.purity) newErrors.purity = "Purity required";

    const grossWeight = parseFloat(formData.grossWeight);
    const netWeight = parseFloat(formData.netWeight);

    if (formData.grossWeight === "" || isNaN(grossWeight) || grossWeight <= 0) {
      newErrors.grossWeight = "Weight required";
    }
    if (formData.netWeight === "" || isNaN(netWeight) || netWeight <= 0) {
      newErrors.netWeight = "Net weight required";
    }
    if (!isNaN(netWeight) && !isNaN(grossWeight) && netWeight > grossWeight) {
      newErrors.netWeight = "Cannot exceed gross";
    }
    if (!formData.karigarCode?.trim())
      newErrors.karigarCode = "Karigar code required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData]);

  const handleSave = useCallback(() => {
    if (validate()) {
      onSave(formData);
    }
  }, [validate, onSave, formData]);

  const handleReSaveClick = useCallback(() => {
    if (validate() && onReSave) {
      onReSave();
    }
  }, [validate, onReSave]);

  const statusConfig = {
    [TAB_STATUS.DRAFT]: { label: "Draft", color: "bg-gray-100 text-gray-600" },
    [TAB_STATUS.QUEUED]: {
      label: "Queued",
      color: "bg-amber-100 text-amber-700",
    },
    [TAB_STATUS.SAVING]: {
      label: "Saving...",
      color: "bg-blue-100 text-blue-700",
    },
    [TAB_STATUS.SAVED]: {
      label: "Saved",
      color: "bg-emerald-100 text-emerald-700",
    },
    [TAB_STATUS.FAILED]: { label: "Failed", color: "bg-red-100 text-red-700" },
    [TAB_STATUS.EDITED]: {
      label: "Edited",
      color: "bg-amber-100 text-amber-700",
    },
  };
  const currentStatus = statusConfig[status] || statusConfig[TAB_STATUS.DRAFT];

  return (
    <div className="space-y-3 sm:space-y-4">
      {/* Status Banner */}
      <StatusBanner
        status={status}
        lastError={lastError}
        onReSave={handleReSaveClick}
      />

      {/* Header Bar */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 bg-white rounded-xl border border-gray-200 px-4 py-3">
        <span
          className={`px-2.5 py-1 text-xs font-medium rounded-full ${currentStatus.color}`}
        >
          {currentStatus.label}
        </span>
        <div className="flex items-center gap-2 w-full sm:w-auto">
          {/* Mode Toggle */}
          <div className="flex items-center bg-gray-100 rounded-lg p-0.5 flex-1 sm:flex-none">
            <button
              type="button"
              onClick={() => setMode("quick")}
              className={`flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-md text-xs sm:text-sm font-medium transition-colors flex-1 sm:flex-none ${
                mode === "quick"
                  ? "bg-white text-gray-900 shadow-sm"
                  : "text-gray-500"
              }`}
            >
              <Zap size={14} />
              <span>Quick</span>
            </button>
            <button
              type="button"
              onClick={() => setMode("advanced")}
              className={`flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-md text-xs sm:text-sm font-medium transition-colors flex-1 sm:flex-none ${
                mode === "advanced"
                  ? "bg-white text-gray-900 shadow-sm"
                  : "text-gray-500"
              }`}
            >
              <Settings size={14} />
              <span>Advanced</span>
            </button>
          </div>

          {canSave && (
            <button
              onClick={
                status === TAB_STATUS.FAILED || status === TAB_STATUS.EDITED
                  ? handleReSaveClick
                  : handleSave
              }
              disabled={isReadOnly}
              className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 bg-gray-900 text-white text-sm font-medium rounded-lg hover:bg-gray-800 disabled:opacity-50"
            >
              {status === TAB_STATUS.FAILED ? (
                <RefreshCw size={14} />
              ) : (
                <Save size={14} />
              )}
              {status === TAB_STATUS.FAILED
                ? "Retry"
                : status === TAB_STATUS.SAVED
                ? "Update"
                : "Save"}
            </button>
          )}
        </div>
      </div>

      {/* Form Content */}
      {mode === "quick" ? (
        <QuickModeForm
          formData={formData}
          errors={errors}
          categories={categories}
          selectedCategoryId={selectedCategoryId}
          availablePurities={availablePurities}
          lockWeights={lockWeights}
          setLockWeights={setLockWeights}
          handleChange={handleChange}
          handleGrossWeightChange={handleGrossWeightChange}
          updateField={updateField}
          handleCategoryChange={handleCategoryChange}
          displayMedia={displayMedia}
          handleMediaChange={handleMediaChange}
          handleMediaUpload={handleMediaUpload}
          handleMediaDelete={handleMediaDelete}
          mediaFiles={mediaFiles}
          locationStocks={locationStocks}
          availableBranches={availableBranches}
          branches={branches}
          permissions={permissions}
          addLocationStock={addLocationStock}
          updateLocationStock={updateLocationStock}
          removeLocationStock={removeLocationStock}
          calculateTotalStock={calculateTotalStock}
          isReadOnly={isReadOnly}
        />
      ) : (
        <AdvancedModeForm
          formData={formData}
          setFormData={setFormData}
          errors={errors}
          categories={categories}
          selectedCategoryId={selectedCategoryId}
          branches={branches}
          availableBranches={availableBranches}
          user={user}
          availablePurities={availablePurities}
          lockWeights={lockWeights}
          setLockWeights={setLockWeights}
          handleChange={handleChange}
          handleGrossWeightChange={handleGrossWeightChange}
          updateField={updateField}
          handleCategoryChange={handleCategoryChange}
          displayMedia={displayMedia}
          handleMediaChange={handleMediaChange}
          handleMediaUpload={handleMediaUpload}
          handleMediaDelete={handleMediaDelete}
          mediaFiles={mediaFiles}
          locationStocks={locationStocks}
          permissions={permissions}
          addLocationStock={addLocationStock}
          updateLocationStock={updateLocationStock}
          removeLocationStock={removeLocationStock}
          calculateTotalStock={calculateTotalStock}
          addStone={addStone}
          updateStone={updateStone}
          removeStone={removeStone}
          isReadOnly={isReadOnly}
        />
      )}
    </div>
  );
};

// ============================================
// QUICK MODE FORM
// ============================================

const QuickModeForm = memo(
  ({
    formData,
    errors,
    categories,
    selectedCategoryId,
    availablePurities,
    lockWeights,
    setLockWeights,
    handleChange,
    handleGrossWeightChange,
    updateField,
    handleCategoryChange,
    displayMedia,
    handleMediaChange,
    handleMediaUpload,
    handleMediaDelete,
    mediaFiles,
    locationStocks,
    availableBranches,
    branches,
    permissions,
    addLocationStock,
    updateLocationStock,
    removeLocationStock,
    calculateTotalStock,
    isReadOnly,
  }) => (
    <div className="space-y-3 sm:space-y-4">
      {/* Main Form Card */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-5 space-y-4 sm:space-y-5">
        {/* Product Name */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">
            Product Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            name="name"
            value={formData.name || ""}
            onChange={handleChange}
            placeholder="Enter product name..."
            disabled={isReadOnly}
            autoFocus
            className={`w-full px-3 py-2.5 text-base border-2 rounded-lg focus:outline-none focus:border-gray-900 ${
              errors.name ? "border-red-300 bg-red-50" : "border-gray-200"
            } ${isReadOnly ? "bg-gray-100 cursor-not-allowed" : ""}`}
          />
          {errors.name && (
            <p className="mt-1 text-xs text-red-600">{errors.name}</p>
          )}
        </div>

        {/* Product Type Cards */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Product Type <span className="text-red-500">*</span>
          </label>
          <div className="grid grid-cols-3 sm:grid-cols-5 gap-1.5 sm:gap-2">
            {PRODUCT_TYPES.map((type) => (
              <TypeCard
                key={type.value}
                type={type}
                isSelected={formData.type === type.value}
                disabled={isReadOnly}
                onClick={() => {
                  updateField("type", type.value);
                  const p = PURITIES[type.value] || PURITIES.GOLD;
                  if (!p.includes(formData.purity)) updateField("purity", p[0]);
                }}
              />
            ))}
          </div>
          {errors.type && (
            <p className="mt-1 text-xs text-red-600">{errors.type}</p>
          )}
        </div>

        {/* Subtype & Category */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <SmartSelect
            label="Subtype"
            name="subType"
            value={formData.subType}
            onChange={handleChange}
            options={PRODUCT_SUBTYPES}
            placeholder="Select subtype"
            required
            error={errors.subType}
            disabled={isReadOnly}
          />
          <CategoryHierarchyDropdown
            categories={categories || []}
            value={selectedCategoryId}
            onChange={handleCategoryChange}
            placeholder="Select category"
            label="Category"
            disabled={isReadOnly}
          />
        </div>

        {/* Purity & Metal Color */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Purity <span className="text-red-500">*</span>
            </label>
            <ToggleGroup
              options={availablePurities}
              value={formData.purity}
              onChange={(v) => updateField("purity", v)}
              disabled={isReadOnly}
            />
            {errors.purity && (
              <p className="mt-1 text-xs text-red-600">{errors.purity}</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Metal Color
            </label>
            <ToggleGroup
              options={METAL_COLORS.slice(0, 3)}
              value={formData.metalColor}
              onChange={(v) => updateField("metalColor", v)}
              disabled={isReadOnly}
            />
          </div>
        </div>

        {/* Weight */}
        <div className="p-3 bg-gray-50 rounded-lg">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Weight <span className="text-red-500">*</span>
          </label>
          <div className="flex items-end gap-2">
            <SmartInput
              label="Gross"
              name="grossWeight"
              type="number"
              value={formData.grossWeight}
              onChange={handleGrossWeightChange}
              suffix="g"
              step="0.001"
              min="0"
              error={errors.grossWeight}
              className="flex-1"
              disabled={isReadOnly}
            />
            <button
              type="button"
              onClick={() => !isReadOnly && setLockWeights(!lockWeights)}
              disabled={isReadOnly}
              className={`p-2 rounded-lg border mb-0.5 ${
                lockWeights
                  ? "bg-gray-900 text-white border-gray-900"
                  : "bg-white text-gray-400 border-gray-200"
              } ${isReadOnly ? "opacity-50 cursor-not-allowed" : ""}`}
            >
              {lockWeights ? <Lock size={16} /> : <Unlock size={16} />}
            </button>
            <SmartInput
              label="Net"
              name="netWeight"
              type="number"
              value={formData.netWeight}
              onChange={handleChange}
              suffix="g"
              step="0.001"
              min="0"
              error={errors.netWeight}
              className="flex-1"
              disabled={isReadOnly}
            />
          </div>
        </div>

        {/* Karigar & Gender */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <SmartInput
            label="Karigar Code"
            name="karigarCode"
            value={formData.karigarCode}
            onChange={handleChange}
            placeholder="KAR001"
            required
            error={errors.karigarCode}
            disabled={isReadOnly}
          />
          <SmartSelect
            label="Gender"
            name="gender"
            value={formData.gender}
            onChange={handleChange}
            options={GENDERS}
            disabled={isReadOnly}
          />
        </div>
      </div>

      {/* Stock Section */}
      <StockManagementSection
        formData={formData}
        locationStocks={locationStocks}
        availableBranches={availableBranches}
        branches={branches}
        permissions={permissions}
        onAddLocation={addLocationStock}
        onUpdateLocation={updateLocationStock}
        onRemoveLocation={removeLocationStock}
        calculateTotalStock={calculateTotalStock}
        onChange={handleChange}
        errors={errors}
        disabled={isReadOnly}
      />

      {/* Media Upload Card */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-5">
        <h4 className="font-medium text-gray-900 mb-3 flex items-center gap-2 text-sm">
          <Image size={16} className="text-gray-500" />
          Product Images (Optional)
        </h4>
        <MediaUpload
          media={displayMedia || []}
          onChange={handleMediaChange}
          onUpload={handleMediaUpload}
          onDelete={handleMediaDelete}
          pendingRemovals={mediaFiles?.pendingRemovals || []}
          maxFiles={5}
          compact={true}
          disabled={isReadOnly}
        />
      </div>

      {/* Info Tip */}
      <div className="flex items-start gap-2.5 p-3 bg-blue-50 rounded-lg">
        <Info size={16} className="text-blue-500 flex-shrink-0 mt-0.5" />
        <p className="text-xs text-blue-700">
          Switch to <strong>Advanced</strong> mode for stones, hallmark,
          certification, and more options.
        </p>
      </div>
    </div>
  )
);
QuickModeForm.displayName = "QuickModeForm";

// ============================================
// ADVANCED MODE FORM
// ============================================

const AdvancedModeForm = memo(
  ({
    formData,
    setFormData,
    errors,
    categories,
    selectedCategoryId,
    branches,
    availableBranches,
    user,
    availablePurities,
    lockWeights,
    setLockWeights,
    handleChange,
    handleGrossWeightChange,
    updateField,
    handleCategoryChange,
    displayMedia,
    handleMediaChange,
    handleMediaUpload,
    handleMediaDelete,
    mediaFiles,
    locationStocks,
    permissions,
    addLocationStock,
    updateLocationStock,
    removeLocationStock,
    calculateTotalStock,
    addStone,
    updateStone,
    removeStone,
    isReadOnly,
  }) => (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4">
      {/* Left Column */}
      <div className="space-y-3 sm:space-y-4">
        {/* Basic Info */}
        <FormSection title="Basic Information" icon={Package} required>
          <div className="space-y-3">
            <SmartInput
              label="Product Name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="e.g., Diamond Ring"
              required
              error={errors.name}
              disabled={isReadOnly}
            />
            <div className="grid grid-cols-2 gap-2">
              <SmartSelect
                label="Type"
                name="type"
                value={formData.type}
                onChange={(e) => {
                  handleChange(e);
                  const p = PURITIES[e.target.value] || PURITIES.GOLD;
                  if (!p.includes(formData.purity)) updateField("purity", p[0]);
                }}
                options={PRODUCT_TYPES.map((t) => ({
                  value: t.value,
                  label: t.label,
                }))}
                required
                error={errors.type}
                disabled={isReadOnly}
              />
              <SmartSelect
                label="Subtype"
                name="subType"
                value={formData.subType}
                onChange={handleChange}
                options={PRODUCT_SUBTYPES}
                required
                error={errors.subType}
                disabled={isReadOnly}
              />
            </div>
            <CategoryHierarchyDropdown
              categories={categories || []}
              value={selectedCategoryId}
              onChange={handleCategoryChange}
              placeholder="Select category"
              label="Category"
              disabled={isReadOnly}
            />
            <div className="grid grid-cols-2 gap-2">
              <SmartSelect
                label="Gender"
                name="gender"
                value={formData.gender}
                onChange={handleChange}
                options={GENDERS}
                disabled={isReadOnly}
              />
              <SmartSelect
                label="Status"
                name="status"
                value={formData.status}
                onChange={handleChange}
                options={STATUSES}
                disabled={isReadOnly}
              />
            </div>
          </div>
        </FormSection>

        {/* Metal & Weight */}
        <FormSection title="Metal & Weight" icon={Scale} required>
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Purity
              </label>
              <ToggleGroup
                options={availablePurities}
                value={formData.purity}
                onChange={(v) => updateField("purity", v)}
                disabled={isReadOnly}
              />
              {errors.purity && (
                <p className="mt-1 text-xs text-red-600">{errors.purity}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Color
              </label>
              <ToggleGroup
                options={METAL_COLORS}
                value={formData.metalColor}
                onChange={(v) => updateField("metalColor", v)}
                disabled={isReadOnly}
              />
            </div>
            <div className="flex items-end gap-2">
              <SmartInput
                label="Gross"
                name="grossWeight"
                type="number"
                value={formData.grossWeight}
                onChange={handleGrossWeightChange}
                suffix="g"
                step="0.001"
                error={errors.grossWeight}
                className="flex-1"
                disabled={isReadOnly}
              />
              <button
                type="button"
                onClick={() => !isReadOnly && setLockWeights(!lockWeights)}
                disabled={isReadOnly}
                className={`p-2 rounded-lg border mb-0.5 ${
                  lockWeights
                    ? "bg-gray-900 text-white border-gray-900"
                    : "bg-white text-gray-400 border-gray-200"
                } ${isReadOnly ? "opacity-50" : ""}`}
              >
                {lockWeights ? <Lock size={16} /> : <Unlock size={16} />}
              </button>
              <SmartInput
                label="Net"
                name="netWeight"
                type="number"
                value={formData.netWeight}
                onChange={handleChange}
                suffix="g"
                step="0.001"
                error={errors.netWeight}
                className="flex-1"
                disabled={isReadOnly}
              />
            </div>
          </div>
        </FormSection>

        {/* Manufacturing */}
        <FormSection title="Manufacturing" icon={Factory}>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <SmartInput
                label="Karigar Code"
                name="karigarCode"
                value={formData.karigarCode}
                onChange={handleChange}
                required
                error={errors.karigarCode}
                disabled={isReadOnly}
              />
              <SmartInput
                label="Batch Number"
                name="manufacturing.batchNumber"
                value={formData.manufacturing?.batchNumber}
                onChange={handleChange}
                disabled={isReadOnly}
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <SmartSelect
                label="Craft Type"
                name="craftType"
                value={formData.craftType}
                onChange={handleChange}
                options={CRAFT_TYPES}
                placeholder="Select craft"
                disabled={isReadOnly}
              />
              <SmartSelect
                label="Technique"
                name="technique"
                value={formData.technique}
                onChange={handleChange}
                options={TECHNIQUES}
                placeholder="Select technique"
                disabled={isReadOnly}
              />
            </div>
          </div>
        </FormSection>

        {/* Stones */}
        <FormSection
          title="Stones & Gems"
          icon={Gem}
          defaultOpen={false}
          badge={formData.stones?.length}
        >
          <StonesSection
            stones={formData.stones || []}
            hasStones={formData.hasStones}
            onAdd={addStone}
            onUpdate={updateStone}
            onRemove={removeStone}
            onToggle={(v) => updateField("hasStones", v)}
            disabled={isReadOnly}
          />
        </FormSection>
      </div>

      {/* Right Column */}
      <div className="space-y-3 sm:space-y-4">
        {/* Product Images */}
        <FormSection title="Product Images" icon={Image}>
          <MediaUpload
            media={displayMedia || []}
            onChange={handleMediaChange}
            onUpload={handleMediaUpload}
            onDelete={handleMediaDelete}
            pendingRemovals={mediaFiles?.pendingRemovals || []}
            maxFiles={10}
            maxSize={10 * 1024 * 1024}
            acceptedTypes={[
              "image/jpeg",
              "image/png",
              "image/webp",
              "image/jpg",
              "video/mp4",
              "video/webm",
              "video/quicktime",
            ]}
            disabled={isReadOnly}
          />
        </FormSection>

        {/* Stock Management */}
        <FormSection title="Stock & Location" icon={Store}>
          <StockManagementSection
            formData={formData}
            locationStocks={locationStocks}
            availableBranches={availableBranches}
            branches={branches}
            permissions={permissions}
            onAddLocation={addLocationStock}
            onUpdateLocation={updateLocationStock}
            onRemoveLocation={removeLocationStock}
            calculateTotalStock={calculateTotalStock}
            onChange={handleChange}
            errors={errors}
            disabled={isReadOnly}
          />
        </FormSection>

        {/* Hallmark */}
        <FormSection
          title="Hallmark & Certification"
          icon={Shield}
          defaultOpen={false}
        >
          <HallmarkManagementSection
            formData={formData}
            onChange={handleChange}
            errors={errors}
            disabled={isReadOnly}
          />
        </FormSection>

        {/* Display Flags */}
        <FormSection title="Display Settings" icon={Eye} defaultOpen={false}>
          <div className="grid grid-cols-2 gap-1.5">
            {["isFeatured", "isNewArrival", "isBestseller", "isExclusive"].map(
              (key) => (
                <label
                  key={key}
                  className={`flex items-center gap-2 p-2.5 rounded-lg cursor-pointer text-sm transition-colors ${
                    formData[key]
                      ? "bg-gray-900 text-white"
                      : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                  } ${isReadOnly ? "opacity-50 cursor-not-allowed" : ""}`}
                >
                  <input
                    type="checkbox"
                    name={key}
                    checked={formData[key] || false}
                    onChange={handleChange}
                    disabled={isReadOnly}
                    className="sr-only"
                  />
                  <span className="font-medium">
                    {key
                      .replace(/^is/, "")
                      .replace(/([A-Z])/g, " $1")
                      .trim()}
                  </span>
                </label>
              )
            )}
          </div>
        </FormSection>

        {/* Description */}
        <FormSection title="Description" icon={FileText} defaultOpen={false}>
          <textarea
            name="description"
            value={formData.description || ""}
            onChange={handleChange}
            rows={4}
            placeholder="Enter product description..."
            disabled={isReadOnly}
            className={`w-full px-3 py-2 border border-gray-200 rounded-lg text-sm resize-none focus:outline-none focus:ring-1 focus:ring-gray-900 focus:border-gray-900 ${
              isReadOnly ? "bg-gray-100 cursor-not-allowed" : ""
            }`}
          />
        </FormSection>
      </div>
    </div>
  )
);
AdvancedModeForm.displayName = "AdvancedModeForm";

// ============================================
// STONES SECTION
// ============================================

const StonesSection = memo(
  ({ stones, hasStones, onAdd, onUpdate, onRemove, onToggle, disabled }) => {
    return (
      <div className="space-y-3">
        <label className="flex items-center gap-2.5 p-2.5 bg-gray-50 rounded-lg cursor-pointer">
          <input
            type="checkbox"
            checked={hasStones || false}
            onChange={(e) => {
              if (disabled) return;
              onToggle(e.target.checked);
              if (e.target.checked && stones.length === 0) onAdd();
            }}
            disabled={disabled}
            className="w-4 h-4 rounded border-gray-300"
          />
          <span className="text-sm font-medium text-gray-700">
            Product has stones/gems
          </span>
        </label>

        {hasStones && (
          <>
            {stones.map((stone, index) => (
              <div key={index} className="p-3 bg-gray-50 rounded-lg space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-gray-700">
                    Stone {index + 1}
                  </span>
                  <button
                    type="button"
                    onClick={() => !disabled && onRemove(index)}
                    disabled={disabled}
                    className={`p-1 text-gray-400 hover:text-red-500 rounded ${
                      disabled ? "opacity-50 cursor-not-allowed" : ""
                    }`}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>

                <div className="grid grid-cols-3 gap-2">
                  <SmartSelect
                    label="Type"
                    value={stone.type}
                    onChange={(e) => onUpdate(index, "type", e.target.value)}
                    options={STONE_TYPES}
                    disabled={disabled}
                  />
                  <SmartSelect
                    label="Shape"
                    value={stone.shape}
                    onChange={(e) => onUpdate(index, "shape", e.target.value)}
                    options={STONE_SHAPES}
                    disabled={disabled}
                  />
                  <SmartSelect
                    label="Setting"
                    value={stone.settingType}
                    onChange={(e) =>
                      onUpdate(index, "settingType", e.target.value)
                    }
                    options={STONE_SETTINGS}
                    disabled={disabled}
                  />
                </div>

                <div className="grid grid-cols-3 gap-2">
                  <SmartInput
                    label="Weight (ct)"
                    type="number"
                    value={stone.weight}
                    onChange={(e) =>
                      onUpdate(index, "weight", parseFloat(e.target.value) || 0)
                    }
                    step="0.001"
                    placeholder="0.000"
                    disabled={disabled}
                  />
                  <SmartInput
                    label="Count"
                    type="number"
                    value={stone.count}
                    onChange={(e) =>
                      onUpdate(index, "count", parseInt(e.target.value) || 1)
                    }
                    min="1"
                    placeholder="1"
                    disabled={disabled}
                  />
                  <SmartInput
                    label="Rate/Carat (₹)"
                    type="number"
                    value={stone.ratePerCarat}
                    onChange={(e) =>
                      onUpdate(
                        index,
                        "ratePerCarat",
                        parseFloat(e.target.value) || 0
                      )
                    }
                    placeholder="0"
                    disabled={disabled}
                  />
                </div>

                {stone.type === "DIAMOND" && (
                  <div className="grid grid-cols-3 gap-2">
                    <SmartInput
                      label="Color"
                      value={stone.color}
                      onChange={(e) => onUpdate(index, "color", e.target.value)}
                      placeholder="D, E, F..."
                      disabled={disabled}
                    />
                    <SmartInput
                      label="Clarity"
                      value={stone.clarity}
                      onChange={(e) =>
                        onUpdate(index, "clarity", e.target.value)
                      }
                      placeholder="VVS1, VS1..."
                      disabled={disabled}
                    />
                    <SmartInput
                      label="Cut"
                      value={stone.cut}
                      onChange={(e) => onUpdate(index, "cut", e.target.value)}
                      placeholder="Excellent..."
                      disabled={disabled}
                    />
                  </div>
                )}

                <div className="flex items-center justify-between p-2 bg-white rounded border">
                  <span className="text-xs text-gray-500">Total Value</span>
                  <span className="text-sm font-medium">
                    ₹{(stone.totalValue || 0).toLocaleString()}
                  </span>
                </div>
              </div>
            ))}

            <button
              type="button"
              onClick={() => !disabled && onAdd()}
              disabled={disabled}
              className={`w-full flex items-center justify-center gap-1.5 p-2.5 border-2 border-dashed border-gray-200 rounded-lg text-gray-500 hover:border-gray-300 hover:text-gray-600 text-sm ${
                disabled ? "opacity-50 cursor-not-allowed" : ""
              }`}
            >
              <Plus size={16} />
              Add Stone
            </button>
          </>
        )}
      </div>
    );
  }
);
StonesSection.displayName = "StonesSection";

export default QuickProductForm;
